import EntityManager from "@/components/EntityManager";
import { today } from "@/lib/format";
import { printCustomerRegister, printFestivalGreetingCard } from "@/lib/print";
import { BRANCHES } from "@/lib/branches";
import { BranchCell } from "@/components/BranchFlag";
import { MessageCircle, Sparkles, Check, Gift, AlertTriangle, History } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const waLink = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  return `https://wa.me/${d.length === 10 ? "91" + d : d}`;
};

const GREETINGS = {
  "Diwali": "✨🪔 Wishing you and your family a very Happy Diwali! May this festival of lights bring prosperity and success to your business. — Nisha Transport Corporation",
  "Pongal": "🌾 Happy Pongal! May this harvest festival bring abundance, joy and prosperity to you and your family. — Nisha Transport Corporation",
  "New Year": "🎉 Wishing you a very Happy New Year! Thank you for your continued trust and partnership. Here's to another successful year together. — Nisha Transport Corporation",
  "Christmas": "🎄 Merry Christmas! Wishing you joy, peace and prosperity this festive season. — Nisha Transport Corporation",
  "Holi": "🌈 Happy Holi! May your life be filled with the colours of joy and success. — Nisha Transport Corporation",
  "Eid": "🌙 Eid Mubarak! Wishing you and your family peace, happiness and prosperity. — Nisha Transport Corporation",
  "Dussehra": "🏹 Happy Dussehra! May the victory of good over evil bring new strength and success to you, your family and your business. — Nisha Transport Corporation",
  "Ayudha Puja": "🙏 Happy Ayudha Puja! May your tools, vehicles and business be blessed with safety, strength and prosperity. — Nisha Transport Corporation",
  "Custom": "",
};

function FestivalGreetings({ rows }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [festival, setFestival] = useState("Diwali");
  const [msg, setMsg] = useState(GREETINGS["Diwali"]);
  const [customName, setCustomName] = useState("");
  const [cardColor, setCardColor] = useState("Festival");
  const [msgEdited, setMsgEdited] = useState(false);
  const [sent, setSent] = useState(() => new Set());
  const [audience, setAudience] = useState("Customers");
  const [staff, setStaff] = useState([]);
  const [manualName, setManualName] = useState("");
  const [manualPhone, setManualPhone] = useState("");
  useEffect(() => {
    if (open && !staff.length) api.get("/staff-master").then((r) => setStaff(r.data || [])).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [branchFilter, setBranchFilter] = useState(() => (user?.role !== "admin" && user?.office ? user.office : "All"));
  const year = new Date().getFullYear();
  const people =
    audience === "Staff"
      ? staff.filter((s) => (s.status || "Active") !== "Inactive").map((s) => ({ id: s.id, name: s.name, phone: s.phone, branch: s.branch || "" }))
      : rows || [];
  const withPhone = people.filter((r) => r.phone && (branchFilter === "All" || (r.branch || "") === branchFilter));
  const logKey = festival === "Custom" ? (customName.trim() || "Custom") : festival;
  const cardFest = festival === "Custom" ? customName.trim() : festival;
  const cardOpts = { color: cardColor === "Festival" ? "" : cardColor, message: msg };
  const loadLog = (f) =>
    api.get(`/greeting-log/${encodeURIComponent(f)}/${year}`).then((r) => setSent(new Set(r.data.customer_ids || []))).catch(() => setSent(new Set()));
  useEffect(() => {
    if (open) loadLog(logKey);
  }, [open, logKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = (r) => {
    window.open(`${waLink(r.phone)}?text=${encodeURIComponent(`Dear ${r.name},\n\n${msg}`)}`, "_blank");
    setSent((prev) => new Set(prev).add(r.id));
    api.post("/greeting-log", { festival: logKey, year, customer_id: r.id }).catch(() => {});
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="festival-greetings-btn">
        <Sparkles className="mr-2 h-4 w-4" /> Festival Greetings
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader><DialogTitle className="font-display">Festival Greetings — WhatsApp</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="w-[130px]" data-testid="greeting-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Customers">Customers</SelectItem>
                  <SelectItem value="Staff">Staff</SelectItem>
                </SelectContent>
              </Select>
              <Select value={festival} onValueChange={(v) => { setFestival(v); setMsg(GREETINGS[v]); setMsgEdited(false); }}>
                <SelectTrigger className="w-[160px]" data-testid="festival-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(GREETINGS).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
              {festival === "Custom" && (
                <Input
                  className="w-[200px]"
                  placeholder="Festival name (e.g. Ayudha Pooja)"
                  value={customName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCustomName(v);
                    if (!msgEdited) setMsg(v.trim() ? `🎉 Happy ${v.trim()}! Wishing you and your family joy, success and prosperity. — Nisha Transport Corporation` : "");
                  }}
                  data-testid="custom-festival-name"
                />
              )}
              <Select value={cardColor} onValueChange={setCardColor}>
                <SelectTrigger className="w-[160px]" data-testid="card-color-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Festival", "Royal Purple", "Festive Gold", "Emerald Green", "Ocean Blue", "Rose Pink", "Teal", "Crimson Red", "Saffron Orange", "Royal Maroon", "Peacock", "Lavender", "Slate Grey"].map((c) => (
                    <SelectItem key={c} value={c}>{c === "Festival" ? "Festival Colours" : c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={branchFilter} onValueChange={setBranchFilter}>
                <SelectTrigger className="w-[140px]" data-testid="greeting-branch-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["All", ...BRANCHES].map((b) => <SelectItem key={b} value={b}>{b === "All" ? "All Branches" : b}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">Each message starts with "Dear &lt;customer name&gt;,"</span>
            </div>
            {cardFest && (
              <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <Button variant="outline" size="sm" className="h-8 border-amber-400 bg-white text-amber-700 hover:bg-amber-100" onClick={() => printFestivalGreetingCard(cardFest, "", cardOpts)} data-testid="festival-card-btn">
                  <Gift className="mr-1.5 h-4 w-4" /> View {cardFest} Card
                </Button>
                <span className="text-xs text-amber-700">Print/save the card once and attach it in each WhatsApp chat. Use the gift icon on a customer for a personalised card.</span>
              </div>
            )}
            <Textarea rows={4} value={msg} onChange={(e) => { setMsg(e.target.value); setMsgEdited(true); }} placeholder="Type your greeting message…" data-testid="greeting-message" />
            <div className="max-h-[260px] space-y-1 overflow-y-auto rounded-md border p-2">
              {withPhone.length === 0 && <div className="py-4 text-center text-sm text-muted-foreground">No {audience.toLowerCase()} with phone numbers{branchFilter !== "All" ? ` in ${branchFilter}` : ""}.</div>}
              {withPhone.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-slate-50" data-testid="greeting-customer-row">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.phone}{r.branch ? ` · ${r.branch}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    {cardFest && (
                      <button
                        type="button"
                        title={`Personalised ${cardFest} card for ${r.name}`}
                        onClick={() => printFestivalGreetingCard(cardFest, r.name, cardOpts)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-amber-600 hover:bg-amber-50"
                        data-testid="customer-festival-card-btn"
                      >
                        <Gift className="h-4 w-4" />
                      </button>
                    )}
                    <Button
                      variant={sent.has(r.id) ? "secondary" : "outline"}
                      size="sm"
                      className="h-8 gap-1.5 text-emerald-600"
                      disabled={!msg.trim()}
                      onClick={() => send(r)}
                      data-testid="send-greeting-btn"
                    >
                      {sent.has(r.id) ? <Check className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
                      {sent.has(r.id) ? "Sent" : "Send"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2 rounded-md border border-dashed p-3" data-testid="manual-greeting-block">
              <div className="text-xs font-semibold text-muted-foreground">Manual send — any name &amp; WhatsApp number (even if not in the list)</div>
              <div className="flex flex-wrap items-center gap-2">
                <Input className="w-[190px]" placeholder="Customer name" value={manualName} onChange={(e) => setManualName(e.target.value)} data-testid="manual-greeting-name" />
                <Input className="w-[170px]" placeholder="WhatsApp number" value={manualPhone} onChange={(e) => setManualPhone(e.target.value)} data-testid="manual-greeting-phone" />
                {cardFest && (
                  <button
                    type="button"
                    title={`Personalised ${cardFest} card${manualName ? ` for ${manualName}` : ""}`}
                    onClick={() => printFestivalGreetingCard(cardFest, manualName.trim(), cardOpts)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-amber-600 hover:bg-amber-50"
                    data-testid="manual-greeting-card-btn"
                  >
                    <Gift className="h-4 w-4" />
                  </button>
                )}
                <Button
                  size="sm"
                  className="h-8 bg-emerald-600 text-white hover:bg-emerald-700"
                  disabled={!manualPhone.trim()}
                  onClick={() => window.open(`${waLink(manualPhone.trim())}?text=${encodeURIComponent(`Dear ${manualName.trim() || "Customer"},\n\n${msg}`)}`, "_blank")}
                  data-testid="manual-greeting-send-btn"
                >
                  <MessageCircle className="mr-1 h-4 w-4" /> Send
                </Button>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">{sent.size} of {withPhone.length} {audience.toLowerCase()} sent for {logKey} {year} — remembered even after you close this. WhatsApp opens per person; tap Send on each.</div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BranchCountChips({ rows }) {
  if (!rows || !rows.length) return null;
  const counts = {};
  rows.forEach((r) => {
    const b = String(r.branch || "").trim() || "No Branch";
    counts[b] = (counts[b] || 0) + 1;
  });
  const keys = [...BRANCHES.filter((b) => counts[b]), ...Object.keys(counts).filter((k) => !BRANCHES.includes(k)).sort()];
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="branch-count-chips">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Customers by branch:</span>
      {keys.map((b) => (
        <span
          key={b}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${b === "No Branch" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 bg-slate-50 text-slate-700"}`}
          data-testid="branch-count-chip"
        >
          {b} <span className="rounded-full bg-white px-1.5 font-bold">{counts[b]}</span>
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-0.5 text-xs font-bold text-primary" data-testid="branch-count-total">
        Total {rows.length}
      </span>
    </div>
  );
}

function DuplicateCodeBanner({ rows }) {
  const byCode = {};
  (rows || []).forEach((r) => {
    const c = String(r.customer_code || "").trim().toUpperCase();
    if (c) (byCode[c] = byCode[c] || []).push(r.name || "—");
  });
  const dups = Object.entries(byCode).filter(([, names]) => names.length > 1);
  if (!dups.length) return null;
  return (
    <div className="mb-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3" data-testid="duplicate-code-banner">
      <div className="flex items-center gap-2 text-sm font-bold text-red-700">
        <AlertTriangle className="h-4 w-4" /> Duplicate customer codes found — please correct:
      </div>
      <ul className="mt-1 space-y-0.5 text-xs text-red-700">
        {dups.map(([code, names]) => (
          <li key={code} data-testid="duplicate-code-item">
            <b>{code}</b> used by {names.join(", ")}
          </li>
        ))}
      </ul>
    </div>
  );
}

function BulkBranchAssign({ selected, reload }) {
  const [saving, setSaving] = useState(false);
  if (!selected.length) return null;
  const assign = async (branch) => {
    setSaving(true);
    try {
      await Promise.all(selected.map((r) => api.put(`/customers/${r.id}`, { ...r, branch })));
      toast.success(`${selected.length} customer(s) → ${branch}`);
      reload && reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Some updates failed");
      reload && reload();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Select value="" onValueChange={assign} disabled={saving}>
      <SelectTrigger className="h-10 w-[190px] border-primary/40 text-sm font-medium text-primary" data-testid="bulk-branch-select">
        <SelectValue placeholder={saving ? "Saving…" : `Set Branch (${selected.length})`} />
      </SelectTrigger>
      <SelectContent>{BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function FixMissingAddresses({ rows, reload }) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [hints, setHints] = useState({});
  const [saving, setSaving] = useState("");
  const missing = (rows || []).filter((r) => !String(r.billing_customer_address || "").trim() || !String(r.address || "").trim() || !String(r.pincode || "").trim());
  if (!missing.length) return null;
  const d = (r) => drafts[r.id] || { address: r.address || "", pincode: r.pincode || "", bca: r.billing_customer_address || "" };
  const onPin = async (r, v) => {
    const pin = v.trim();
    setDrafts((s) => ({ ...s, [r.id]: { ...d(r), ...(s[r.id] || {}), pincode: pin } }));
    if (!/^\d{6}$/.test(pin)) return;
    setHints((h) => ({ ...h, [r.id]: "Looking up…" }));
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const j = await res.json();
      const po = j?.[0]?.PostOffice?.[0];
      if (po) {
        const city = po.Block && po.Block !== "NA" ? po.Block : po.District;
        setHints((h) => ({ ...h, [r.id]: `${city}, ${po.State}` }));
        setDrafts((s) => {
          const cur = s[r.id] || { address: r.address || "", pincode: pin };
          return { ...s, [r.id]: { ...cur, pincode: pin, address: String(cur.address || "").trim() ? cur.address : city } };
        });
      } else setHints((h) => ({ ...h, [r.id]: "Pincode not found" }));
    } catch {
      setHints((h) => ({ ...h, [r.id]: "" }));
    }
  };
  const save = async (r) => {
    const v = d(r);
    if (!String(v.address || "").trim() && !String(v.pincode || "").trim() && !String(v.bca || "").trim()) {
      toast.info("Type an address, pincode or billing customer address first");
      return;
    }
    setSaving(r.id);
    try {
      await api.put(`/customers/${r.id}`, { ...r, address: v.address, pincode: v.pincode, billing_customer_address: v.bca });
      toast.success(`${r.name} address saved`);
      reload && reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving("");
    }
  };
  return (
    <>
      <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setOpen(true)} data-testid="fill-addresses-btn">
        <AlertTriangle className="mr-2 h-4 w-4" /> Fill Addresses ({missing.length})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[640px]" data-testid="fill-addresses-dialog">
          <DialogHeader><DialogTitle className="font-display">Fill Addresses — {missing.length} customer(s) missing address / pincode / billing customer address</DialogTitle></DialogHeader>
          <div className="max-h-[400px] space-y-1 overflow-y-auto rounded-md border p-2">
            {missing.map((r) => (
              <div key={r.id} className="rounded px-2 py-1.5 hover:bg-slate-50" data-testid="fill-address-row">
                <div className="flex items-center gap-3">
                  <div className="w-[180px] min-w-0 shrink-0">
                    <div className="truncate text-sm font-medium">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.customer_code || "—"}</div>
                  </div>
                  <Input className="h-8 w-[110px] text-sm" placeholder="Pincode" value={d(r).pincode} onChange={(e) => onPin(r, e.target.value)} data-testid="fill-pincode-input" />
                  <Input className="h-8 flex-1 text-sm" placeholder="City / address" value={d(r).address} onChange={(e) => setDrafts((s) => ({ ...s, [r.id]: { ...d(r), ...(s[r.id] || {}), address: e.target.value } }))} data-testid="fill-address-input" />
                  <Button size="sm" className="h-8 shrink-0" onClick={() => save(r)} disabled={saving === r.id} data-testid="fill-address-save-btn">
                    {saving === r.id ? "Saving…" : "Save"}
                  </Button>
                </div>
                <div className="mt-1 flex items-center gap-3 pl-[192px]">
                  <Input className="h-8 flex-1 text-sm" placeholder="Billing Customer Address (shown in Outstanding Report) e.g. Sricity-517646" value={d(r).bca} onChange={(e) => setDrafts((s) => ({ ...s, [r.id]: { ...d(r), ...(s[r.id] || {}), bca: e.target.value } }))} data-testid="fill-bca-input" />
                </div>
                {hints[r.id] && <div className="pl-[192px] pt-0.5 text-xs text-emerald-700" data-testid="fill-pincode-hint">{hints[r.id]}</div>}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Type a 6-digit pincode — the city auto-fills from India Post. Fill the Billing Customer Address line for the Outstanding Report. Fully filled customers disappear from this list.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

function FixMissingCodes({ rows, reload }) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState("");
  const missing = (rows || []).filter((r) => !String(r.customer_code || "").trim());
  if (!missing.length) return null;
  const save = async (r) => {
    const code = String(drafts[r.id] || "").trim();
    if (!code) {
      toast.info("Type a customer code first");
      return;
    }
    const clash = (rows || []).find((x) => x.id !== r.id && String(x.customer_code || "").trim().toLowerCase() === code.toLowerCase());
    if (clash) {
      toast.error(`Code "${code}" is already used by ${clash.name}`);
      return;
    }
    setSaving(r.id);
    try {
      await api.put(`/customers/${r.id}`, { ...r, customer_code: code });
      toast.success(`${r.name} → code ${code} saved`);
      reload && reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving("");
    }
  };
  return (
    <>
      <Button variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50" onClick={() => setOpen(true)} data-testid="fill-codes-btn">
        <AlertTriangle className="mr-2 h-4 w-4" /> Fill Codes ({missing.length})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px]" data-testid="fill-codes-dialog">
          <DialogHeader><DialogTitle className="font-display">Fill Customer Codes — {missing.length} customer(s) without a code</DialogTitle></DialogHeader>
          <div className="max-h-[400px] space-y-1 overflow-y-auto rounded-md border p-2">
            {missing.map((r) => (
              <div key={r.id} className="flex items-center gap-3 rounded px-2 py-1.5 hover:bg-slate-50" data-testid="fill-code-row">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{r.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{r.branch || "—"}{r.phone ? ` · ${r.phone}` : ""}</div>
                </div>
                <Input
                  className="h-8 w-[140px] text-sm uppercase"
                  placeholder="e.g. AC01"
                  value={drafts[r.id] || ""}
                  onChange={(e) => setDrafts((s) => ({ ...s, [r.id]: e.target.value.toUpperCase() }))}
                  onKeyDown={(e) => e.key === "Enter" && save(r)}
                  data-testid="fill-code-input"
                />
                <Button size="sm" className="h-8 shrink-0" onClick={() => save(r)} disabled={saving === r.id} data-testid="fill-code-save-btn">
                  {saving === r.id ? "Saving…" : "Save"}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Codes must be unique — duplicates are blocked. Press Enter to save. Coded customers disappear from this list.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

function FixBlankBranches({ rows, reload }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState("");
  const blanks = (rows || []).filter((r) => !String(r.branch || "").trim());
  if (!blanks.length) return null;
  const assign = async (r, branch) => {
    setSaving(r.id);
    try {
      await api.put(`/customers/${r.id}`, { ...r, branch });
      toast.success(`${r.name} → ${branch}`);
      reload && reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving("");
    }
  };
  return (
    <>
      <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => setOpen(true)} data-testid="fix-blank-branches-btn">
        <AlertTriangle className="mr-2 h-4 w-4" /> Fix Blank Branches ({blanks.length})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle className="font-display">Assign Branch — {blanks.length} customer(s) without branch</DialogTitle></DialogHeader>
          <div className="max-h-[360px] space-y-1 overflow-y-auto rounded-md border p-2">
            {blanks.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded px-2 py-1.5 hover:bg-slate-50" data-testid="blank-branch-row">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.customer_code || "—"}{r.phone ? ` · ${r.phone}` : ""}</div>
                </div>
                <Select value="" onValueChange={(v) => assign(r, v)} disabled={saving === r.id}>
                  <SelectTrigger className="h-8 w-[150px] text-sm" data-testid="blank-branch-select">
                    <SelectValue placeholder={saving === r.id ? "Saving…" : "Select branch"} />
                  </SelectTrigger>
                  <SelectContent>{BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Pick a branch — it saves instantly and the customer disappears from this list.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BranchLogDialog() {
  const [open, setOpen] = useState(false);
  const [log, setLog] = useState(null);
  useEffect(() => {
    if (open) api.get("/customer-branch-log").then((r) => setLog(r.data || [])).catch(() => setLog([]));
  }, [open]);
  const fmtTs = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
  const exportLog = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = ["Sl No.,Date & Time,Customer Name,Old Branch,New Branch,Changed By"];
    (log || []).forEach((e, i) => lines.push([i + 1, fmtTs(e.changed_at), e.customer_name || "", e.old_branch || "No Branch", e.new_branch || "No Branch", e.changed_by || ""].map(cell).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `branch-change-log-${today()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="branch-log-btn">
        <History className="mr-2 h-4 w-4" /> Branch Log
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <DialogTitle className="font-display">Branch Change Log</DialogTitle>
              <Button variant="outline" size="sm" onClick={exportLog} disabled={!log || !log.length} data-testid="branch-log-excel-btn">
                Excel
              </Button>
            </div>
          </DialogHeader>
          <div className="max-h-[380px] overflow-y-auto rounded-md border">
            {log === null && <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>}
            {log && log.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No branch changes recorded yet.</div>}
            {(log || []).map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 border-b px-3 py-2 text-sm last:border-0" data-testid="branch-log-row">
                <div className="min-w-0">
                  <div className="truncate font-medium">{e.customer_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{fmtTs(e.changed_at)}{e.changed_by ? ` · by ${e.changed_by}` : ""}</div>
                </div>
                <div className="whitespace-nowrap text-xs font-bold">
                  <span className={e.old_branch ? "" : "text-amber-600"}>{e.old_branch || "No Branch"}</span>
                  <span className="mx-1 text-muted-foreground">→</span>
                  <span className="text-primary">{e.new_branch || "No Branch"}</span>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const FIELD_LABELS = {
  name: "Customer Name", customer_code: "Customer Code", contact_person: "Contact Person", phone: "Mobile No.",
  email: "Email Id", state: "State", pincode: "Pin Code", gstin: "GSTIN", pan_no: "PAN No", tan_no: "TAN No",
  msme_no: "MSME No", cin_no: "CIN No", address: "Billing Address", billing_customer_address: "Billing Customer Address",
};

function ChangeLogDialog() {
  const [open, setOpen] = useState(false);
  const [log, setLog] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (open) api.get("/customer-change-log").then((r) => setLog(r.data || [])).catch(() => setLog([]));
  }, [open]);
  const fmtTs = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
  const shown = (log || []).filter((e) => !q.trim() || String(e.customer_name || "").toLowerCase().includes(q.trim().toLowerCase()));
  const exportLog = () => {
    const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = ["Sl No.,Date & Time,Customer Name,Field,Old Value,New Value,Changed By"];
    let i = 0;
    shown.forEach((e) => (e.changes || []).forEach((c) => lines.push([++i, fmtTs(e.changed_at), e.customer_name || "", FIELD_LABELS[c.field] || c.field, c.old || "", c.new || "", e.changed_by || ""].map(cell).join(","))));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `customer-change-log-${today()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="change-log-btn">
        <History className="mr-2 h-4 w-4" /> Change Log
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[640px]" data-testid="change-log-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <DialogTitle className="font-display">Customer Change Log</DialogTitle>
              <Button variant="outline" size="sm" onClick={exportLog} disabled={!shown.length} data-testid="change-log-excel-btn">
                Excel
              </Button>
            </div>
          </DialogHeader>
          <Input className="h-8" placeholder="Filter by customer name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="change-log-filter-input" />
          <div className="max-h-[380px] overflow-y-auto rounded-md border">
            {log === null && <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>}
            {log && shown.length === 0 && <div className="py-6 text-center text-sm text-muted-foreground">No edits recorded yet — changes are logged from now on whenever a customer is updated.</div>}
            {shown.map((e) => (
              <div key={e.id} className="border-b px-3 py-2 text-sm last:border-0" data-testid="change-log-row">
                <div className="flex items-center justify-between gap-3">
                  <div className="truncate font-medium">{e.customer_name || "—"}</div>
                  <div className="whitespace-nowrap text-xs text-muted-foreground">{fmtTs(e.changed_at)}{e.changed_by ? ` · by ${e.changed_by}` : ""}</div>
                </div>
                <div className="mt-0.5 space-y-0.5">
                  {(e.changes || []).map((c, i) => (
                    <div key={i} className="text-xs">
                      <span className="font-semibold">{FIELD_LABELS[c.field] || c.field}:</span>{" "}
                      <span className="text-muted-foreground line-through">{c.old || "(blank)"}</span>
                      <span className="mx-1 text-muted-foreground">→</span>
                      <span className="font-medium text-primary">{c.new || "(blank)"}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const exportCustomersCsv = (list) => {
  const headers = ["Sl No.", "Customer Code", "Customer Name", "Branch", "Contact Person", "Mobile No.", "Email Id", "State", "Pin Code", "GSTIN", "PAN No", "TAN No", "MSME No", "CIN No", "Billing Address", "Billing Customer Address"];
  const cell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [headers.join(",")];
  list.forEach((r, i) => lines.push([i + 1, r.customer_code || "", r.name || "", r.branch || "", r.contact_person || "", r.phone || "", r.email || "", r.state || "", r.pincode || "", r.gstin || "", r.pan_no || "", r.tan_no || "", r.msme_no || "", r.cin_no || "", r.address || "", r.billing_customer_address || ""].map(cell).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ntc-customers-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
  "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh",
  "Lakshadweep", "Puducherry",
];

export default function Customers() {
  return (
    <EntityManager
      title="Customer Master"
      subtitle="Add and manage clients you bill for freight services."
      endpoint="customers"
      entityName="Customer"
      selectable
      selectedActions={(selected, reload) => <BulkBranchAssign selected={selected} reload={reload} />}
      filters={[
        {
          key: "q",
          label: "Search",
          type: "text",
          placeholder: "Name / code / phone / GSTIN / branch",
          match: (r, v) => {
            const q = String(v || "").trim().toLowerCase();
            if (!q) return true;
            return [r.name, r.customer_code, r.contact_person, r.phone, r.email, r.gstin, r.pan_no, r.branch, r.state]
              .some((x) => String(x || "").toLowerCase().includes(q));
          },
        },
        {
          key: "branchf",
          label: "Branch",
          type: "select",
          options: ["All Branches", ...BRANCHES],
          match: (r, v) => !v || v === "All Branches" || (r.branch || "") === v,
        },
        {
          key: "nobranch",
          label: "Branch Gap",
          toggleLabel: "No Branch only",
          type: "toggle",
          match: (r, v) => !v || !String(r.branch || "").trim(),
          badge: (rows) => rows.filter((r) => !String(r.branch || "").trim()).length,
        },
        {
          key: "noaddress",
          label: "Address Gap",
          toggleLabel: "No Address only",
          type: "toggle",
          match: (r, v) => !v || (!String(r.billing_customer_address || "").trim() && (!String(r.address || "").trim() || !String(r.pincode || "").trim())),
          badge: (rows) => rows.filter((r) => !String(r.billing_customer_address || "").trim() && (!String(r.address || "").trim() || !String(r.pincode || "").trim())).length,
        },
      ]}
      exportCsv={exportCustomersCsv}
      banner={(rows) => (
        <>
          <BranchCountChips rows={rows} />
          <DuplicateCodeBanner rows={rows} />
        </>
      )}
      headerExtra={(rows, _d, _f, reload) => (
        <>
          <FixBlankBranches rows={rows} reload={reload} />
          <FixMissingAddresses rows={rows} reload={reload} />
          <FixMissingCodes rows={rows} reload={reload} />
          <BranchLogDialog />
          <ChangeLogDialog />
          <FestivalGreetings rows={rows} />
        </>
      )}
      onPrintSelected={(list) => printCustomerRegister(list)}
      defaults={{ name: "", customer_code: "", contact_person: "", phone: "", email: "", address: "", state: "", pincode: "", gstin: "", pan_no: "", tan_no: "", msme_no: "", cin_no: "", billing_customer_address: "", branch: "" }}
      fields={[
        { key: "customer_code", label: "Customer code", type: "text", placeholder: "Leave blank to auto-generate (e.g. CUST-1001)" },
        { key: "name", label: "Company / Client name", type: "text", placeholder: "Acme Industries Pvt Ltd" },
        { key: "branch", label: "Branch", type: "select", options: BRANCHES, placeholder: "Select branch" },
        { key: "contact_person", label: "Contact person", type: "text", placeholder: "Rahul Sharma" },
        { key: "phone", label: "Phone", type: "text", placeholder: "+91 98765 43210" },
        { key: "email", label: "Email", type: "text", placeholder: "billing@acme.com" },
        { key: "birthday", label: "Birthday (owner/contact)", type: "date" },
        { key: "gstin", label: "GSTIN", type: "text", placeholder: "27AABCU9603R1ZP" },
        { key: "pan_no", label: "PAN No", type: "text", placeholder: "AABCU9603R" },
        { key: "tan_no", label: "TAN No", type: "text", placeholder: "CHEA12345B" },
        { key: "msme_no", label: "MSME No", type: "text", placeholder: "UDYAM-TN-02-0123456" },
        { key: "cin_no", label: "CIN Number", type: "text", placeholder: "U63090TN2005PTC055577" },
        { key: "state", label: "State", type: "select", options: INDIAN_STATES, placeholder: "Select state", clearable: true, clearLabel: "No State (remove)" },
        { key: "pincode", label: "Pin code", type: "text", placeholder: "600001" },
        { key: "address", label: "Billing Address", type: "textarea", placeholder: "Full billing address" },
        { key: "billing_customer_address", label: "Billing Customer Address (Shown in Outstanding Report)", type: "text", placeholder: "e.g. Sricity-517646" },
      ]}
      columns={[
        { key: "_sl", label: "SL No.", render: (r, idx) => (typeof idx === "number" ? idx + 1 : "—") },
        { key: "branch", label: "Branch", render: (r) => <BranchCell value={r.branch} /> },
        {
          key: "name",
          label: "Customer Name",
          render: (r) => (
            <span className="inline-flex items-center gap-1.5">
              {r.name}
              {!String(r.billing_customer_address || "").trim() && (!String(r.address || "").trim() || !String(r.pincode || "").trim()) && (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" title="Address or pincode missing — billing address autofill won't work" data-testid="customer-no-address-flag">
                  No address
                </span>
              )}
              {!String(r.customer_code || "").trim() && (
                <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700" title="No customer code — add one via Fill Codes for faster lookups" data-testid="customer-no-code-flag">
                  No code
                </span>
              )}
            </span>
          ),
        },
        { key: "customer_code", label: "Customer Code" },
        { key: "contact_person", label: "Contact Person", render: (r) => r.contact_person || "—" },
        { key: "phone", label: "Mobile No.", render: (r) => r.phone || "—" },
        { key: "email", label: "Email Id", render: (r) => r.email || "—" },
        { key: "state", label: "State" },
        { key: "gstin", label: "GSTIN" },
        { key: "pan_no", label: "PAN No", render: (r) => r.pan_no || "—" },
        { key: "tan_no", label: "TAN No", render: (r) => r.tan_no || "—" },
        { key: "msme_no", label: "MSME No", render: (r) => r.msme_no || "—" },
        { key: "cin_no", label: "CIN No", render: (r) => r.cin_no || "—" },
        { key: "billing_customer_address", label: "Billing Customer Address", render: (r) => r.billing_customer_address || "—" },
        {
          key: "whatsapp",
          label: "WhatsApp",
          render: (r) =>
            r.phone ? (
              <a
                href={waLink(r.phone)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                title={`WhatsApp ${r.name}`}
                data-testid="customer-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4" />
              </a>
            ) : (
              "—"
            ),
        },
      ]}
    />
  );
}
